import { useContext, useRef, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ApartmentViewContext } from '../context/ApartmentViewContext';
import {
	arrangeModeName,
	hotSpotModeName,
	infoModeName,
	insolationModeName,
	isometricModeName,
	stylesModeName,
} from '../context/contextStrings';
import styles from '../../../styles/aprt.module.scss';

function InfoTooltip() {
	const { t } = useTranslation();
	const { infoTooltipData, setInfoTooltipData } =
		useContext(ApartmentViewContext);
	const { type, hovered, position, data } = infoTooltipData;
	const tooltipRef = useRef(null);
	const [tooltipHeight, setTooltipHeight] = useState(0);
	const [tooltipWidth, setTooltipWidth] = useState(0);
	const [tooltipClass, setTooltipClass] = useState('');

	useEffect(() => {
		if (tooltipRef.current) {
			setTooltipHeight(tooltipRef.current.offsetHeight);
			setTooltipWidth(tooltipRef.current.offsetWidth);
		}
	}, [type, hovered]);

	useEffect(() => {
		if (tooltipHeight > 0 && position) {
			const spaceBelow = window.innerHeight - (position.top + position.height);

			if (spaceBelow < tooltipHeight + 12) {
				setTooltipClass(styles.tooltip_top);
			} else {
				setTooltipClass(styles.tooltip_bottom);
			}
		}
	}, [tooltipHeight, tooltipWidth, position]);

	useEffect(() => {
		const hideTooltip = () => {
			if (infoTooltipData.hovered) {
				setInfoTooltipData({
					...infoTooltipData,
					type: '',
					hovered: false,
					position: null,
					data: [],
				});
			}
		};

		if (hovered) {
			window.addEventListener('scroll', hideTooltip, { passive: true });
			window.addEventListener('touchmove', hideTooltip, { passive: true });
		}

		return () => {
			window.removeEventListener('scroll', hideTooltip);
			window.removeEventListener('touchmove', hideTooltip);
		};
	}, [hovered, infoTooltipData]);

	if (!hovered || !type) {
		return null;
	}

	let topPosition = position.top + position.height + 12;
	if (tooltipClass.includes(styles.tooltip_top)) {
		topPosition = position.top - tooltipHeight - 12;
	}

	let tooltipHorizontalTransform = '-50%';
	let tooltipArrowHorizontalTranslate = '-50%';
	const halfTooltipWidth = tooltipWidth / 2;
	const spaceLeft = position.left + position.width / 2;
	const spaceRight = window.innerWidth - spaceLeft;

	if (spaceRight < halfTooltipWidth) {
		const rightOverflow = halfTooltipWidth - spaceRight;
		tooltipHorizontalTransform = `calc(-50% - ${rightOverflow + 12}px)`;
		tooltipArrowHorizontalTranslate = `calc(-50% + ${rightOverflow + 12}px)`;
	} else if (spaceLeft < halfTooltipWidth) {
		const leftOverflow = halfTooltipWidth - spaceLeft;
		tooltipHorizontalTransform = `calc(-50% + ${leftOverflow + 12}px)`;
		tooltipArrowHorizontalTranslate = `calc(-50% - ${leftOverflow + 12}px)`;
	} else tooltipHorizontalTransform = '-50%';

	const tooltipOffset = {
		position: 'fixed',
		top: `${topPosition}px`,
		left: `${spaceLeft}px`,
		transform: `translate(${tooltipHorizontalTransform})`,
		'--tooltip-arrow-transform': `${tooltipArrowHorizontalTranslate}`,
	};

		const getDisplayPrice = (num) => {
		if (num === null || num === undefined) return '-';

		return parseFloat(num).toLocaleString('pl-PL', {
			style: 'currency',
			currency: 'PLN',
			minimumFractionDigits: 2,
			maximumFractionDigits: 2,
		});
	};

	return (
		<div
			ref={tooltipRef}
			className={`${styles.infoTooltip} ${tooltipClass}`}
			style={tooltipOffset}
		>
			{type === t('insolation') && (
				<>
					<p className={styles.infoTooltip__paragraph}>
						{t('insolation_info_1')}
					</p>
					<p className={styles.infoTooltip__disclaimer}>
						{t('calculation_info')}
					</p>
				</>
			)}
			{type === t('exposition') && (
				<>
					<p className={styles.infoTooltip__paragraph}>
						{t('exposition_info_1')}
					</p>
					<p className={styles.infoTooltip__paragraph}>
						{t('exposition_info_2')}
					</p>
				</>
			)}
			{type === t('windows') && (
				<>
					<p className={styles.infoTooltip__paragraph}>{t('windows_info_2')}</p>
					<p className={styles.infoTooltip__paragraph}>{t('windows_info_1')}</p>
					<p className={styles.infoTooltip__disclaimer}>
						{t('calculation_info')}
					</p>
				</>
			)}
			{type === 'priceHistory' && (
				<>
					<p className={styles.infoTooltip__paragraph}>
						{t('price_history')}
					</p>
					<div style={{
						display: 'grid',
						gridTemplateColumns: 'repeat(3, minmax(6rem, 1fr))',
						columnGap: '0.5rem',
					}}>
						<div>{t('price_of_sqm')}:</div>
						<div>{t('price_total')}:</div>
						<div>{t('price_since')}:</div>
						{data.map((entry, index) => {
							const date = entry.date
								? new Date(entry.date).toLocaleString('pl-PL', {
										year: 'numeric',
										month: '2-digit',
										day: '2-digit',
										// hour: '2-digit',
										// minute: '2-digit',
									})
								: '-';
							return (
								<>
									<div key={index * data.length}>
										{getDisplayPrice(entry.price_m2)}
									</div>
									<div key={index * data.length + 1}>
										{getDisplayPrice(entry.price_brutto)}
									</div>
									<div key={index * data.length + 2}>
										{date}
									</div>
								</>
							);
						})}
					</div>
				</>
			)}
			{type === 'rotateToNorth' && (
				<>
					<p className={styles.infoTooltip__paragraph}>
						{t('rotate_to_north_info')}
					</p>
				</>
			)}
			{type === stylesModeName && (
				<>
					<p className={styles.infoTooltip__paragraph}>
						{t('styles_mode_info')}
					</p>
				</>
			)}
			{type === infoModeName && (
				<>
					<p className={styles.infoTooltip__paragraph}>{t('info_mode_info')}</p>
				</>
			)}
			{type === arrangeModeName && (
				<>
					<p className={styles.infoTooltip__paragraph}>
						{t('arrange_mode_info')}
					</p>
				</>
			)}
			{type === insolationModeName && (
				<>
					<p className={styles.infoTooltip__paragraph}>
						{t('insolation_mode_info')}
					</p>
				</>
			)}
			{type === hotSpotModeName && (
				<>
					<p className={styles.infoTooltip__paragraph}>
						{t('hot_spots_mode_info')}
					</p>
				</>
			)}
			{type === isometricModeName && (
				<>
					<p className={styles.infoTooltip__paragraph}>
						{t("isometric_mode_info")}
					</p>
				</>
			)}
		</div>
	);
}

export default InfoTooltip;
